import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useStream } from "@/lib/useStream";
import { BLOG as T } from "@/constants/testIds";

const BLANK = { slug: null, title: "", excerpt: "", body: "", products: [], status: "draft" };
const TAGS = /\[\[([a-z0-9-]+)\]\]/g;
const input = "w-full px-4 py-2.5 rounded-2xl border border-[#E7E0D3] bg-white focus:outline-none focus:ring-2 focus:ring-[#E06D44]/40";

// Splits Claude's "TITLE: / EXCERPT: / body" reply into the form fields.
const parseDraft = (text) => {
  const title = text.match(/^TITLE:\s*(.+)$/m)?.[1]?.trim() || "";
  const excerpt = text.match(/^EXCERPT:\s*(.+)$/m)?.[1]?.trim() || "";
  const body = text.replace(/^TITLE:.*$/m, "").replace(/^EXCERPT:.*$/m, "").trim();
  return { title, excerpt, body };
};

const AiDraft = ({ products, onDraft }) => {
  const [picks, setPicks] = useState(["", "", ""]);
  const [angle, setAngle] = useState("");
  const { busy, run } = useStream();
  const draft = async () => {
    const chosen = picks.filter(Boolean);
    if (!chosen.length) return toast.error("Pick at least one product");
    try {
      await run("/admin/blog/ai-draft", { products: chosen, angle }, (t) => onDraft({ ...parseDraft(t), products: chosen }));
      toast.success("Draft ready. Check it over, then save.");
    } catch (e) { toast.error(e.message); }
    return null;
  };
  return (
    <div className="bg-[#FDF2EC] rounded-3xl p-5 space-y-3">
      <p className="font-heading font-bold flex items-center gap-2"><Sparkles size={16} className="text-[#E06D44]" /> Draft an article with AI</p>
      <div className="grid sm:grid-cols-3 gap-2">
        {picks.map((v, i) => (
          <select key={i} data-testid={T.aiProduct(i)} value={v} onChange={(e) => setPicks((l) => l.map((x, j) => (j === i ? e.target.value : x)))} className={input}>
            <option value="">{i === 0 ? "Pick a product" : "Another product (optional)"}</option>
            {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
          </select>
        ))}
      </div>
      <input data-testid={T.aiAngle} value={angle} onChange={(e) => setAngle(e.target.value)} maxLength={300} placeholder="Angle (optional), e.g. best toys for a rainy day" className={input} />
      <button data-testid={T.aiDraft} onClick={draft} disabled={busy} className="px-5 py-2.5 rounded-full bg-[#E06D44] text-white text-sm font-semibold disabled:opacity-60 hover:bg-[#C95B33] transition-colors">{busy ? "Writing…" : "Write draft"}</button>
      <p className="text-xs text-stone-500">AI drafts are saved as drafts only. Nothing goes live until you publish it.</p>
    </div>
  );
};

const Editor = ({ post, setPost, onSaved, onDeleted, activeSlugs }) => {
  const [busy, setBusy] = useState(false);
  const save = async (status) => {
    setBusy(true);
    const body = { title: post.title, excerpt: post.excerpt, body: post.body, status, products: [...new Set([...(post.products || []), ...[...post.body.matchAll(TAGS)].map((m) => m[1])])].slice(0, 8) };
    try {
      const { data } = post.slug ? await api.put(`/admin/blog/${post.slug}`, body) : await api.post("/admin/blog", body);
      onSaved(data);
      toast.success(status === "published" ? "Published. It's live on /blog." : "Saved as draft");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/blog/${post.slug}`).then(() => onDeleted(post.slug)).catch((e) => toast.error(errMsg(e)));
  };
  const set = (k) => (e) => setPost({ ...post, [k]: e.target.value });
  const unknown = [...new Set([...post.body.matchAll(TAGS)].map((m) => m[1]))].filter((s) => !activeSlugs.has(s));
  return (
    <div className="bg-white rounded-3xl border border-[#F0EBE1] p-5 space-y-3">
      <input data-testid={T.adminTitle} value={post.title} onChange={set("title")} maxLength={120} placeholder="Title" className={`${input} font-heading font-bold`} />
      <textarea data-testid={T.adminExcerpt} value={post.excerpt} onChange={set("excerpt")} maxLength={300} rows={2} placeholder="One-sentence summary (shown on Google and the blog page)" className={input} />
      <textarea data-testid={T.adminBody} value={post.body} onChange={set("body")} rows={16} placeholder={"Article text. Start headings with ## and put a product on its own line like [[ruff-dawg-minnow]]"} className={`${input} font-mono text-sm`} />
      {unknown.length > 0 && <p data-testid="admin-blog-unknown-products" className="text-xs text-[#B45309]">These product tags won't show a card because the product doesn't exist or is hidden: {unknown.join(", ")}</p>}
      <div className="flex flex-wrap gap-2">
        <button data-testid={T.adminSave} onClick={() => save("draft")} disabled={busy} className="px-5 py-2.5 rounded-full border border-[#E7E0D3] text-sm font-semibold hover:bg-[#F0EBE1]">Save draft</button>
        <button data-testid={T.adminPublish} onClick={() => save("published")} disabled={busy} className="px-5 py-2.5 rounded-full bg-[#1C1917] text-white text-sm font-semibold hover:bg-[#E06D44]">{post.status === "published" ? "Update live article" : "Publish"}</button>
        {post.slug && <button data-testid={T.adminDelete} onClick={remove} className="ml-auto px-4 py-2.5 rounded-full text-sm text-stone-500 hover:text-[#E11D48] inline-flex items-center gap-1"><Trash2 size={14} /> Delete</button>}
      </div>
    </div>
  );
};

export const AdminBlog = ({ products }) => {
  const [posts, setPosts] = useState([]);
  const [post, setPost] = useState(BLANK);
  useEffect(() => { api.get("/admin/blog").then(({ data }) => setPosts(data)).catch(() => {}); }, []);
  const saved = (d) => { setPosts((l) => [d, ...l.filter((p) => p.slug !== d.slug)]); setPost(d); };
  return (
    <section className="space-y-6">
      <div data-testid={T.adminList} className="bg-white rounded-3xl border border-[#F0EBE1] divide-y divide-[#F0EBE1]">
        {posts.map((p) => (
          <button key={p.slug} data-testid={T.adminEdit(p.slug)} onClick={() => setPost(p)} className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-[#FDFBF7]">
            <span data-testid={T.adminRow(p.slug)} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase ${p.status === "published" ? "bg-[#4E7B68] text-white" : "bg-[#F0EBE1] text-stone-600"}`}>{p.status}</span>
            <span className="font-medium truncate">{p.title}</span>
            {p.status === "published" && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="ml-auto text-xs text-[#E06D44] underline">View</a>}
          </button>
        ))}
        <button data-testid={T.adminNew} onClick={() => setPost(BLANK)} className="w-full text-left px-5 py-3 text-sm font-semibold text-[#E06D44] hover:bg-[#FDFBF7]">+ New article</button>
      </div>
      {!post.slug && <AiDraft products={[...products].sort((a, b) => a.name.localeCompare(b.name))} onDraft={(d) => setPost((p) => ({ ...p, ...d }))} />}
      <Editor post={post} setPost={setPost} activeSlugs={new Set(products.filter((p) => p.active !== false).map((p) => p.slug))} onSaved={saved} onDeleted={(s) => { setPosts((l) => l.filter((p) => p.slug !== s)); setPost(BLANK); }} />
    </section>
  );
};
